import * as t from 'io-ts';
import _, {compact, flatten} from 'lodash';

import {makeApiType, RCardGroupIdRef} from './apiTypes';

/*
 * Runtime type.
 */

const RCardGroupDirty = t.readonly(
  t.type({
    id: t.Integer,
    url: t.string,
    namespace: t.string,
    alias: t.string,
    name: t.string,
    numberOfCards: t.Integer,
    updatedAt: t.Integer,
    createdAt: t.Integer,
  }),
  'cardGroup',
);

/*
 * Type.
 */

export interface CardGroupModel extends t.TypeOf<typeof RCardGroupDirty> {}
export const RCardGroup = t.clean<CardGroupModel>(RCardGroupDirty as any);
export const RCardGroups = t.readonlyArray(RCardGroup);
RCardGroupIdRef.setTargetType(RCardGroup);

/*
 * IO methods.
 */

export function parseCardGroup(src: any): CardGroupModel {
  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    alias: src.alias,
    name: src.name,
    numberOfCards: src.num_cards,
    updatedAt: src.updated_at || 0,
    createdAt: src.created_at || 0,
  };
}

function parseCardGroups(src: any) {
  return src.card_groups && src.card_groups.map(parseCardGroup);
}

export function parseCardGroupsFromConversation(src: any): ReadonlyArray<CardGroupModel> {
  return _(src.trackers)
    .map((tracker) => tracker && tracker.recipient)
    .concat(src.contact)
    .compact()
    .map((recipient) => compact([recipient?.card?.groups, recipient?.fallback?.card?.groups])[0])
    .flatten()
    .compact()
    .map((group) => parseCardGroup(group))
    .uniqBy((group) => group.id)
    .value();
}

export function parseCardGroupsFromRecipient(recipient: any): ReadonlyArray<CardGroupModel> {
  // Parse the main card groups, if any.
  const parsedCardGroups = recipient?.card?.groups?.map((group: any) => parseCardGroup(group));

  // Parse the fallback card groups, if any.
  const fallbackCard = recipient.fallback && recipient.fallback.card;
  const parsedFallbackCardGroups = fallbackCard?.groups?.map((group: any) => parseCardGroup(group));

  // Return what we found.
  return compact(flatten([parsedCardGroups, parsedFallbackCardGroups]));
}

/*
 * API type.
 */

export const ApiCardGroup = makeApiType('cardGroup', RCardGroup, parseCardGroup);
export const ApiCardGroups = makeApiType('cardGroups', RCardGroups, parseCardGroups);
